import { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Package, Tag, Layers, BarChart3, Gift } from 'lucide-react';
import { AddProductDialog } from '@/components/products/AddProductDialog';
import { ProductsTable } from '@/components/products/ProductsTable';
import { ProductStats } from '@/components/products/ProductStats';
import { ProductFilters, ProductFiltersState } from '@/components/products/ProductFilters';
import { CategoryOverview } from '@/components/products/CategoryOverview';
import { PricingTiersChart } from '@/components/products/PricingTiersChart';
import { ProductImportExport } from '@/components/products/ProductImportExport';
import { AddGlobalPricingRuleDialog } from '@/components/products/AddGlobalPricingRuleDialog';
import { GlobalPricingRulesTable } from '@/components/products/GlobalPricingRulesTable';
import { ProductAnalyticsDashboard } from '@/components/products/ProductAnalyticsDashboard';
import { ProductBundlesManager } from '@/components/products/ProductBundlesManager';
import { BundleUsageAnalytics } from '@/components/products/BundleUsageAnalytics';
import { BundlePerformanceChart } from '@/components/products/BundlePerformanceChart';
import { BundleConversionAnalytics } from '@/components/products/BundleConversionAnalytics';
import { BundleComparisonTool } from '@/components/deals/BundleComparisonTool';
import { BundleEffectivenessScoring } from '@/components/products/BundleEffectivenessScoring';
import { BundleABTesting } from '@/components/products/BundleABTesting';
import { BundleSeasonalityAnalysis } from '@/components/products/BundleSeasonalityAnalysis';
import { BundlePromotionEngine } from '@/components/products/BundlePromotionEngine';
import { BundlePerformanceAlerts } from '@/components/products/BundlePerformanceAlerts';
import { BundleCannibalizationAnalysis } from '@/components/products/BundleCannibalizationAnalysis';
import { useProducts, usePricingRules } from '@/hooks/useProducts';

export default function Products() {
  const { data: products } = useProducts();
  const { data: pricingRules } = usePricingRules();

  const categories = useMemo(() => {
    const cats = new Set(products?.map((p) => p.category).filter(Boolean) as string[]);
    return Array.from(cats);
  }, [products]);

  const tiers = useMemo(() => {
    const tierSet = new Set(products?.map((p) => p.tier).filter(Boolean) as string[]);
    return Array.from(tierSet);
  }, [products]);

  const maxPrice = useMemo(() => {
    return Math.max(...(products?.map((p) => p.base_price) || [10000]), 10000);
  }, [products]);

  const [filters, setFilters] = useState<ProductFiltersState>({
    search: '',
    category: 'all',
    tier: 'all',
    status: 'all',
    minPrice: 0,
    maxPrice: maxPrice,
  });

  const filteredProducts = useMemo(() => {
    if (!products) return [];
    return products.filter((product) => {
      const matchesSearch =
        filters.search === '' ||
        product.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        product.sku?.toLowerCase().includes(filters.search.toLowerCase()) ||
        product.description?.toLowerCase().includes(filters.search.toLowerCase());
      const matchesCategory =
        filters.category === 'all' || product.category === filters.category;
      const matchesTier = filters.tier === 'all' || product.tier === filters.tier;
      const matchesStatus =
        filters.status === 'all' ||
        (filters.status === 'active' && product.is_active) ||
        (filters.status === 'inactive' && !product.is_active);
      const matchesPrice =
        product.base_price >= filters.minPrice &&
        (filters.maxPrice === 0 || product.base_price <= filters.maxPrice);
      return matchesSearch && matchesCategory && matchesTier && matchesStatus && matchesPrice;
    });
  }, [products, filters]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Products & Pricing</h1>
          <p className="text-muted-foreground">Manage your product catalog, bundles, and pricing rules</p>
        </div>
        <div className="flex gap-2">
          <ProductImportExport />
          <AddProductDialog />
        </div>
      </div>

      <ProductStats products={products} pricingRules={pricingRules} />

      <Tabs defaultValue="products" className="space-y-4">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="products">
            <Package className="h-4 w-4 mr-2" />
            Products
          </TabsTrigger>
          <TabsTrigger value="bundles">
            <Gift className="h-4 w-4 mr-2" />
            Bundles
          </TabsTrigger>
          <TabsTrigger value="categories">
            <Layers className="h-4 w-4 mr-2" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="pricing">
            <Tag className="h-4 w-4 mr-2" />
            Pricing Rules
          </TabsTrigger>
        </TabsList>

        <TabsContent value="products" className="space-y-4">
          <ProductFilters
            filters={filters}
            onFiltersChange={setFilters}
            categories={categories}
            tiers={tiers}
            maxPriceLimit={maxPrice}
          />
          <Card data-wizard="products">
            <CardHeader>
              <CardTitle>Product Catalog</CardTitle>
              <CardDescription>
                {filteredProducts.length} of {products?.length || 0} products
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ProductsTable products={filteredProducts} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="bundles" className="space-y-6">
          <ProductBundlesManager />
          <BundlePerformanceAlerts />
          <BundlePromotionEngine />
          <BundleEffectivenessScoring />
          <BundleCannibalizationAnalysis />
          <BundleABTesting />
          <BundleSeasonalityAnalysis />
          <BundleComparisonTool />
          <BundleConversionAnalytics />
          <BundlePerformanceChart />
          <BundleUsageAnalytics />
        </TabsContent>

        <TabsContent value="categories">
          <CategoryOverview products={products} />
        </TabsContent>

        <TabsContent value="analytics">
          <ProductAnalyticsDashboard />
        </TabsContent>

        <TabsContent value="pricing">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Global Pricing Rules</CardTitle>
                <CardDescription>
                  Rules that apply across all products. Product-specific rules can be managed from each product.
                </CardDescription>
              </div>
              <AddGlobalPricingRuleDialog />
            </CardHeader>
            <CardContent>
              <GlobalPricingRulesTable />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
